// Side-by-side (split) text diff: aligned two-column rows, word-level
// highlighting inside changed lines, swap-sides toggle, and prev/next
// navigation between changed blocks.
//
// Extracted from TemplateCompareModal so it works on any two strings, not just
// two template versions fetched from the templates API. TemplateCompareModal
// (template version vs. version) and the Support "Test Note" flow (production
// note vs. test note) both render this between their own column headers.

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Button } from "../ui/Button";
import { ChevronUp, ChevronDown, ArrowLeftRight } from "lucide-react";
import { computeWordDiff } from "./TrackChangesEditor";

const MAX_LINE_PRODUCT = 4000000;
// Also guards asymmetric cases (e.g. n huge, m tiny) that would pass the
// product check while still allocating n+1 row arrays for the LCS table.
const MAX_TOTAL_LINES = 20000;

const computeLineOpsFallback = (a, b) => {
  const n = a.length;
  const m = b.length;
  let start = 0;
  while (start < n && start < m && a[start] === b[start]) start += 1;
  let endA = n;
  let endB = m;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA -= 1;
    endB -= 1;
  }
  const ops = [];
  for (let i = 0; i < start; i += 1) ops.push({ type: "unchanged", text: a[i] });
  for (let i = start; i < endA; i += 1) ops.push({ type: "removed", text: a[i] });
  for (let j = start; j < endB; j += 1) ops.push({ type: "added", text: b[j] });
  for (let i = endA; i < n; i += 1) ops.push({ type: "unchanged", text: a[i] });
  return ops;
};

// Line-level LCS returning an ordered list of
// { type: 'unchanged' | 'removed' | 'added', text } describing how to turn
// `prevText` (left/older) into `currText` (right/newer).
const computeLineOps = (prevText, currText) => {
  const a = (prevText || "").split("\n");
  const b = (currText || "").split("\n");
  const n = a.length;
  const m = b.length;

  if (n * m > MAX_LINE_PRODUCT || n + m > MAX_TOTAL_LINES) {
    return computeLineOpsFallback(a, b);
  }

  const lcs = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lcs[i][j] =
        a[i] === b[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const ops = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ type: "unchanged", text: b[j] });
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      ops.push({ type: "removed", text: a[i] });
      i += 1;
    } else {
      ops.push({ type: "added", text: b[j] });
      j += 1;
    }
  }
  while (i < n) {
    ops.push({ type: "removed", text: a[i] });
    i += 1;
  }
  while (j < m) {
    ops.push({ type: "added", text: b[j] });
    j += 1;
  }
  return ops;
};

// Build aligned rows for the two-column view. Each row has a left and right
// cell; changed blocks pair removed (left) with added (right) lines positionally
// and word-highlight the differences within paired lines.
//   row = {
//     leftNum, rightNum,            // 1-based line numbers (null when absent)
//     leftType, rightType,          // 'removed' | 'added' | 'unchanged' | 'empty'
//     leftSegments, rightSegments,  // [{ text, changed }]
//     changed                       // true if this row contains a difference
//   }
export const computeSplitDiffRows = (prevText, currText) => {
  const ops = computeLineOps(prevText, currText);
  const rows = [];
  let leftNum = 0;
  let rightNum = 0;

  const plainSeg = (text) => [{ text, changed: false }];

  let k = 0;
  while (k < ops.length) {
    const op = ops[k];

    if (op.type === "unchanged") {
      leftNum += 1;
      rightNum += 1;
      rows.push({
        leftNum,
        rightNum,
        leftType: "unchanged",
        rightType: "unchanged",
        leftSegments: plainSeg(op.text),
        rightSegments: plainSeg(op.text),
        changed: false,
      });
      k += 1;
      continue;
    }

    // Gather a contiguous block of removed then added lines.
    const removed = [];
    const added = [];
    while (k < ops.length && ops[k].type === "removed") {
      removed.push(ops[k].text);
      k += 1;
    }
    while (k < ops.length && ops[k].type === "added") {
      added.push(ops[k].text);
      k += 1;
    }

    const maxLen = Math.max(removed.length, added.length);
    for (let p = 0; p < maxLen; p += 1) {
      const oldLine = p < removed.length ? removed[p] : null;
      const newLine = p < added.length ? added[p] : null;

      if (oldLine != null && newLine != null) {
        leftNum += 1;
        rightNum += 1;
        const tokens = computeWordDiff(oldLine, newLine);
        rows.push({
          leftNum,
          rightNum,
          leftType: "removed",
          rightType: "added",
          leftSegments: tokens
            .filter((t) => t.type !== "added")
            .map((t) => ({ text: t.text, changed: t.type === "removed" })),
          rightSegments: tokens
            .filter((t) => t.type !== "removed")
            .map((t) => ({ text: t.text, changed: t.type === "added" })),
          changed: true,
        });
      } else if (oldLine != null) {
        leftNum += 1;
        rows.push({
          leftNum,
          rightNum: null,
          leftType: "removed",
          rightType: "empty",
          leftSegments: plainSeg(oldLine),
          rightSegments: [],
          changed: true,
        });
      } else {
        rightNum += 1;
        rows.push({
          leftNum: null,
          rightNum,
          leftType: "empty",
          rightType: "added",
          leftSegments: [],
          rightSegments: plainSeg(newLine),
          changed: true,
        });
      }
    }
  }

  return rows;
};

const cellBgClass = (type) => {
  if (type === "removed") return "bg-red-50";
  if (type === "added") return "bg-green-50";
  if (type === "empty") return "bg-gray-50/60";
  return "";
};

const wordClass = (type, changed) => {
  if (!changed) return "";
  if (type === "removed") return "bg-red-200 text-red-900 rounded-sm";
  if (type === "added") return "bg-green-200 text-green-900 rounded-sm";
  return "";
};

const DiffCell = ({ type, num, segments }) => (
  <div className={`flex ${cellBgClass(type)}`}>
    <span className="select-none w-10 shrink-0 pr-2 text-right text-xs text-gray-400 border-r border-gray-200">
      {num != null ? num : ""}
    </span>
    <span className="flex-1 px-3 whitespace-pre-wrap break-words">
      {segments && segments.length ? (
        segments.map((s, idx) =>
          s.text ? (
            <span key={idx} className={wordClass(type, s.changed)}>
              {s.text}
            </span>
          ) : null
        )
      ) : (
        <span>{"\u00A0"}</span>
      )}
    </span>
  </div>
);

// `leftText`/`rightText` are already in display order — callers that offer a
// swap toggle pass the swapped values and handle `onToggleSwap` themselves, so
// their column headers stay in sync with the columns rendered here.
//
// `open` drives the auto-jump to the first diff and the arrow-key bindings;
// pass the host dialog's open state so a closed dialog doesn't capture keys.
const SplitDiffView = ({
  leftText,
  rightText,
  open = true,
  onToggleSwap,
  identicalMessage = "These two versions are identical.",
}) => {
  const rows = useMemo(
    () => computeSplitDiffRows(leftText, rightText),
    [leftText, rightText]
  );

  const changeCount = useMemo(
    () => rows.filter((r) => r.changed).length,
    [rows]
  );

  // Group consecutive changed rows into "blocks" so the user can jump between
  // them. `diffBlockStarts[k]` is the row index that begins the k-th block;
  // `rowBlockIndex[i]` is the block a given row belongs to (-1 if unchanged).
  const { diffBlockStarts, rowBlockIndex } = useMemo(() => {
    const starts = [];
    const map = new Array(rows.length).fill(-1);
    let inBlock = false;
    rows.forEach((row, idx) => {
      if (row.changed) {
        if (!inBlock) {
          starts.push(idx);
          inBlock = true;
        }
        map[idx] = starts.length - 1;
      } else {
        inBlock = false;
      }
    });
    return { diffBlockStarts: starts, rowBlockIndex: map };
  }, [rows]);

  const [activeBlock, setActiveBlock] = useState(-1);
  const blockRefs = useRef([]);

  const goToBlock = useCallback(
    (target) => {
      if (!diffBlockStarts.length) return;
      const clamped = Math.max(0, Math.min(target, diffBlockStarts.length - 1));
      setActiveBlock(clamped);
      const el = blockRefs.current[clamped];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    },
    [diffBlockStarts]
  );

  // Automatically jump to the first diff once the rows are rendered (applies to
  // every compare). Re-runs when the text changes or sides are swapped.
  useEffect(() => {
    if (!open || !diffBlockStarts.length) {
      setActiveBlock(-1);
      return undefined;
    }
    const raf = requestAnimationFrame(() => goToBlock(0));
    return () => cancelAnimationFrame(raf);
  }, [open, rows, diffBlockStarts, goToBlock]);

  useEffect(() => {
    if (!open || !diffBlockStarts.length) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        goToBlock(activeBlock + 1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        goToBlock(activeBlock - 1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, diffBlockStarts, activeBlock, goToBlock]);

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {onToggleSwap && (
            <Button
              variant="outline"
              size="sm"
              onClick={onToggleSwap}
              className="text-sm px-3 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
              title="Swap left and right sides"
            >
              <ArrowLeftRight className="h-4 w-4" />
              Swap sides
            </Button>
          )}
          <span className="text-sm text-gray-500">
            {changeCount === 0
              ? "No differences"
              : `${changeCount} ${
                  changeCount === 1 ? "line" : "lines"
                } changed`}
          </span>
        </div>
        {diffBlockStarts.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              {activeBlock >= 0
                ? `Diff ${activeBlock + 1} of ${diffBlockStarts.length}`
                : `${diffBlockStarts.length} diff${
                    diffBlockStarts.length === 1 ? "" : "s"
                  }`}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToBlock(activeBlock <= 0 ? 0 : activeBlock - 1)}
              disabled={activeBlock <= 0}
              className="text-sm px-3 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-40"
              title="Previous diff (↑)"
            >
              <ChevronUp className="h-4 w-4" />
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToBlock(activeBlock + 1)}
              disabled={activeBlock >= diffBlockStarts.length - 1}
              className="text-sm px-3 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-40"
              title="Next diff (↓)"
            >
              <ChevronDown className="h-4 w-4" />
              Next
            </Button>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 border rounded-md overflow-y-auto font-mono text-sm leading-relaxed">
        {rows.length > 0 ? (
          rows.map((row, idx) => {
            const blockIdx = rowBlockIndex[idx];
            const isBlockStart =
              blockIdx >= 0 && diffBlockStarts[blockIdx] === idx;
            const isActive = blockIdx >= 0 && blockIdx === activeBlock;
            return (
              <div
                key={idx}
                ref={
                  isBlockStart
                    ? (el) => {
                        blockRefs.current[blockIdx] = el;
                      }
                    : undefined
                }
                className={`grid grid-cols-2 scroll-mt-2 ${
                  isActive ? "ring-2 ring-inset ring-ktx-purple" : ""
                }`}
              >
                <div className="border-r border-gray-200">
                  <DiffCell
                    type={row.leftType}
                    num={row.leftNum}
                    segments={row.leftSegments}
                  />
                </div>
                <DiffCell
                  type={row.rightType}
                  num={row.rightNum}
                  segments={row.rightSegments}
                />
              </div>
            );
          })
        ) : (
          <p className="p-4 text-gray-500">{identicalMessage}</p>
        )}
      </div>
    </>
  );
};

export default SplitDiffView;
